/* eslint-disable react/forbid-prop-types */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { currencies } from '../../../../utilities/constants';
import { FLOWS, sendStats } from '../../../../services/mixpanel';
import { MXP_FTC_REVIEW_VISIT, MXP_FTC_REVIEW_CONTINUE } from '../../mixpanel-events';
import { useUserPersonalInfo } from '../../../../utilities/sharedHooks/user';

const LoanOffer = ({ screensKeys, calculatorForm, openScreen }) => {
  const { t: translate } = useTranslation();
  const { isUS } = useUserPersonalInfo();

  useEffect(() => {
    sendStats(MXP_FTC_REVIEW_VISIT, {}, FLOWS.FTC);
  }, []);

  const renderFooter = () => (
    <button
      className="btn btn-primary mb-4"
      type="button"
      onClick={() => {
        sendStats(MXP_FTC_REVIEW_CONTINUE, {}, FLOWS.FTC);
        openScreen(screensKeys.LOAN_TERMS);
      }}>
      {translate('continue_btn_title')}
    </button>
  );

  const principalAmount = calculatorForm?.principalAmount;
  const { principalWallet, collateralWallet } = calculatorForm;
  const principalCurrency = principalWallet?.currency_code;
  const collateralCurrency = collateralWallet.coin;

  const requiredCollateralAmountFiat = principalAmount / (calculatorForm?.ltv / 100.0);

  return (
    <ModalBodyLayout
      withBack
      title={translate(`loan_review_offer_title`)}
      renderFooter={renderFooter}
      onPrevClick={() => openScreen(isUS ? screensKeys.AVERAGE_INCOME : screensKeys.ADD_LOAN)}>
      <div className="d-flex flex-row justify-content-between w-100 mt-4">
        <div>
          <b className="text-primary text-f4 upper-case">{`${principalAmount} ${principalCurrency}`}</b>
          <p className="text-shadow text-f6">{translate('loan_review_offer_receive_note')}</p>
        </div>
        <div className="text-right">
          <b className="text-f4">
            {`${calculatorForm?.loanLength} ${translate('loan_review_offer_month_label')}`}{' '}
          </b>
          <p className="text-shadow text-f6">{translate('loan_review_offer_term_length_note')}</p>
        </div>
      </div>
      <div className="rounded-extra px-3 py-3 bg-light flex-row-center mt-4">
        <p className="text-f5 text-shadow">{translate('loans_payment_total_collateral_label')}</p>
        <div className="text-right">
          <p className="text-f5 text-dark  upper-case">
            <b>{`${formatNumber(
              calculatorForm?.collateralAmount,
              currencies[collateralCurrency].decimal
            )} ${collateralCurrency}`}</b>
          </p>
          <p className="text-f6 text-shadow upper-case">
            {`${formatNumberWithComma(
              formatNumber(requiredCollateralAmountFiat, currencies[principalCurrency].decimal)
            )} ${principalCurrency}`}
          </p>
        </div>
      </div>
      <p className="text-shadow text-f5 mt-2">{translate(`loan_review_offer_note`)}</p>

      <div className="rounded-extra bg-light mt-3 card-calculator__summary">
        <div className="d-flex flex-column p-0 mb-2">
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_review_offer_loan_to_value_label')}
              </p>
              <b className="text-f6">{`${calculatorForm?.ltv}%`}</b>
            </div>
          </div>
          <hr className="m-0" />
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_review_offer_apr_label')}
              </p>
              <b className="text-f6">{`${calculatorForm?.apr}%`}</b>
            </div>
          </div>
          <hr className="m-0" />
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_review_offer_total_interest_label')}
              </p>

              <b className="text-f6">{`${
                calculatorForm?.totalInterest
              } ${principalCurrency?.toUpperCase()}`}</b>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-extra bg-light mt-3 card-calculator__summary">
        <div className="d-flex flex-column p-0 mb-2">
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_review_offer_pay_off_date_label')}
              </p>
              <b className="text-f6">
                {moment(new Date())
                  .add(calculatorForm?.loanLength, 'M')
                  .format(isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY')}
              </b>
            </div>
          </div>
          <hr className="m-0" />
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_review_offer_total_payment_label')}
              </p>
              <b className="text-f6">
                {`${formatNumberWithComma(
                  formatNumber(
                    parseFloat(calculatorForm?.principalAmount) +
                      parseFloat(calculatorForm?.totalInterest),
                    currencies[principalCurrency].decimal
                  )
                )} ${principalCurrency?.toUpperCase()}`}
              </b>
            </div>
          </div>
        </div>
      </div>
    </ModalBodyLayout>
  );
};

export default LoanOffer;

LoanOffer.propTypes = {
  screensKeys: PropTypes.object.isRequired,
  calculatorForm: PropTypes.object.isRequired,
  openScreen: PropTypes.func.isRequired,
};
